import { useState, useRef } from "react";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { CloseIcon } from "../../services/icons";
import { Button, Dialog, Input, toastMessage } from "../utils";
import { buttonClasses } from "../utils/theme";
import { encryptUsingAES256, decryptUsingAES256 } from "../../services/functions";
import { useGetReportMutation } from "../../api/api";

// ─── Utility: Format raw data as compact text for Claude ─────────────────────
function formatDataForClaude(data) {
	if (!data || (Array.isArray(data) && data.length === 0)) return "";

	// If data is an array of objects (JSON rows)
	if (Array.isArray(data) && typeof data[0] === "object") {
		const headers = Object.keys(data[0]);
		const lines = [headers.join(" | ")];
		data.forEach((row) => {
			lines.push(headers.map((h) => row[h] ?? "").join(" | "));
		});
		return lines.join("\n");
	}

	// If data is a string (HTML or other), extract text from HTML table
	if (typeof data === "string" && data.includes("<")) {
		const parser = new DOMParser();
		const doc = parser.parseFromString(data, "text/html");
		const table = doc.querySelector("table");
		if (table) {
			const headers = [];
			table.querySelectorAll("thead th, thead td").forEach((th) => headers.push(th.textContent.trim()));
			const lines = [headers.join(" | ")];
			table.querySelectorAll("tbody tr").forEach((tr) => {
				const row = [];
				tr.querySelectorAll("td").forEach((td) => row.push(td.textContent.trim()));
				if (row.length > 0) lines.push(row.join(" | "));
			});
			return lines.join("\n");
		}
	}

	// Fallback: stringify the data
	return typeof data === "string" ? data : JSON.stringify(data, null, 2);
}

// Max ~20,000 chars of data to stay well within token limits
const MAX_DATA_CHARS = 20000;

// ─── Utility: Fetch all report data via paginated API calls ──────────────────
async function fetchAllReportData(makePayload, fetchReport, onProgress, cancelRef) {
	const pagesize = 50;

	onProgress({ text: "Fetching data... (page 1)", percent: 0, page: 1, totalPages: null });
	const payload = makePayload("html");
	payload.pagesize = pagesize;
	payload.page_no = 1;

	const firstResult = await fetchReport(payload).unwrap();
	if (firstResult.error) {
		throw new Error(firstResult.message || "Failed to fetch report data");
	}

	const totalCount = firstResult.totalCount || 0;
	const totalPages = Math.ceil(totalCount / pagesize);
	const firstData = firstResult.data || "";

	let allFormattedData = formatDataForClaude(firstData);
	let allRowCount = Array.isArray(firstData) ? firstData.length : allFormattedData.split("\n").length - 1;
	let truncated = false;

	for (let page = 2; page <= totalPages; page++) {
		if (cancelRef.current) throw new Error("CANCELLED");

		// Check if we already have enough data
		if (allFormattedData.length >= MAX_DATA_CHARS) {
			truncated = true;
			break;
		}

		const percent = Math.round(((page - 1) / totalPages) * 100);
		onProgress({ text: `Fetching data... (page ${page} of ${totalPages})`, percent, page, totalPages });
		const pagePayload = { ...payload, page_no: page };
		const result = await fetchReport(pagePayload).unwrap();
		if (result.error) continue;

		const pageData = result.data || "";
		const pageFormatted = formatDataForClaude(pageData);
		// Skip the header line for subsequent pages
		const dataLines = pageFormatted.split("\n").slice(1).join("\n");
		allFormattedData += "\n" + dataLines;
		allRowCount += Array.isArray(pageData) ? pageData.length : dataLines.split("\n").length;
	}

	onProgress({ text: "All data fetched. Generating AI report...", percent: 100, page: totalPages, totalPages });

	if (allFormattedData.length > MAX_DATA_CHARS) {
		allFormattedData = allFormattedData.substring(0, MAX_DATA_CHARS) + "\n... (data truncated)";
		truncated = true;
	}

	return { csvData: allFormattedData, totalCount, rowCount: allRowCount, truncated };
}

// ─── Utility: Build prompt for Claude ────────────────────────────────────────
function buildPrompt(csvData, reportPayload, totalCount, rowCount, truncated) {
	const startDate = reportPayload.startdate
		? new Date(reportPayload.startdate).toLocaleDateString()
		: "N/A";
	const endDate = reportPayload.enddate
		? new Date(reportPayload.enddate).toLocaleDateString()
		: "N/A";

	const truncationNote = truncated
		? `\nNOTE: Data was truncated to ${rowCount} rows out of ${totalCount} total records for analysis. Extrapolate insights for the full dataset where appropriate.`
		: "";

	return `You are a data analytics expert. Analyze the following gate automation / vehicle movement report data and generate a comprehensive, self-contained HTML report.

DATA (pipe-delimited table, ${rowCount} rows${truncated ? ` of ${totalCount} total` : ""}):
${csvData}
${truncationNote}

REPORT CONTEXT:
- Date Range: ${startDate} to ${endDate}
- Total Records: ${totalCount}
- System: Gate Automation System (RFID + ANPR based)

REQUIREMENTS:
1. Generate a COMPLETE, self-contained HTML document (<!DOCTYPE html> to </html>)
2. ALL CSS must be inline in a <style> tag within <head> (no external stylesheets)
3. ALL charts must be inline SVG elements (no external JS libraries like Chart.js or D3)
4. The report must be optimized for printing (include @media print styles)
5. Return ONLY the HTML document, no other text or explanation

REPORT STRUCTURE:
1. **Header**: Report title "AI Analytics Report", date range, generation timestamp
2. **Executive Summary**: 3-5 key findings in bullet points
3. **Key Metrics Cards**: Total entries, unique vehicles, busiest checkpoint, peak hours etc.
4. **Charts & Visualizations** (all as inline SVG):
   - Pie chart showing distribution by checkpoint or category
   - Bar chart showing volume/count trends
   - Any other relevant visualization based on data patterns
5. **Detailed Data Table**: Clean styled summary table of the key metrics
6. **Insights & Observations**: Statistical analysis, patterns, anomalies
7. **Recommendations**: Based on the data patterns

STYLING:
- Professional color scheme: primary #FF7200 (orange), secondary #333333, accent #4CAF50
- Font: Arial, Helvetica, sans-serif
- Responsive tables with alternating row colors (#f9f9f9 / #ffffff)
- SVG charts: 450px wide, centered, with text labels and legends
- Cards for key metrics with subtle shadows
- @media print rules: hide unnecessary elements, page-break-inside: avoid on charts
- Page margins appropriate for A4 printing

Return ONLY the complete HTML document.`;
}

// ─── Utility: Call Claude API ────────────────────────────────────────────────
async function callClaudeAPI(apiKey, csvData, reportPayload, totalCount, rowCount, truncated) {
	const prompt = buildPrompt(csvData, reportPayload, totalCount, rowCount, truncated);

	const response = await fetch("https://api.anthropic.com/v1/messages", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"x-api-key": apiKey,
			"anthropic-version": "2023-06-01",
			"anthropic-dangerous-direct-browser-access": "true",
		},
		body: JSON.stringify({
			model: "claude-sonnet-4-5-20250929",
			max_tokens: 16000,
			messages: [{ role: "user", content: prompt }],
		}),
	});

	if (!response.ok) {
		const errorBody = await response.json().catch(() => ({}));
		if (response.status === 401) {
			throw new Error("401");
		}
		throw new Error(errorBody.error?.message || `API error: ${response.status}`);
	}

	const result = await response.json();
	const content = result.content?.[0]?.text || "";

	// Extract HTML from response — Claude may wrap in ```html code blocks
	const codeBlockMatch = content.match(/```html\n?([\s\S]*?)```/);
	if (codeBlockMatch) return codeBlockMatch[1].trim();

	const docMatch = content.match(/(<!DOCTYPE[\s\S]*<\/html>)/i);
	if (docMatch) return docMatch[1].trim();

	if (content.includes("<html") || content.includes("<!DOCTYPE")) {
		return content.trim();
	}

	throw new Error("NO_HTML");
}

// ─── Component: API Key Dialog ───────────────────────────────────────────────
function ApiKeyDialog({ open, onClose, onSave }) {
	const [apiKey, setApiKey] = useState("");
	const [showKey, setShowKey] = useState(false);

	const handleSave = () => {
		const trimmed = apiKey.trim();
		if (!trimmed) {
			toastMessage(false, "Please enter a valid API key");
			return;
		}
		if (!trimmed.startsWith("sk-ant-")) {
			toastMessage(false, "Invalid API key format. Key should start with 'sk-ant-'");
			return;
		}
		const encrypted = encryptUsingAES256(trimmed);
		localStorage.setItem("claude_api_key", encrypted);
		toastMessage(true, "API key saved successfully");
		onSave(trimmed);
		onClose();
	};

	return (
		<Dialog
			open={open}
			handleClose={onClose}
			handleSubmit={handleSave}
			title="Configure Claude API Key"
			size="sm"
			SubmitText="Save Key"
		>
			<p style={{ color: "var(--text-color)", marginBottom: "1rem", fontSize: "14px" }}>
				Enter your Anthropic API key to enable AI-powered report generation. The key will be encrypted and stored locally in your browser.
			</p>
			<Input
				name="apiKey"
				label="Claude API Key"
				type={showKey ? "text" : "password"}
				value={apiKey}
				onChange={(e) => setApiKey(e.target.value)}
				placeholder="sk-ant-..."
				adornment={{
					is: true,
					type: showKey ? "eye" : "eyeClose",
					onClick: () => setShowKey(!showKey),
				}}
			/>
		</Dialog>
	);
}

// ─── Component: AI Report Viewer (Full-screen) ──────────────────────────────
function AIReportViewer({ open, onClose, html }) {
	const iframeRef = useRef(null);

	const handleSavePdf = () => {
		const iframe = iframeRef.current;
		if (iframe?.contentWindow) {
			iframe.contentWindow.print();
		}
	};

	return (
		<MuiDialog fullScreen open={open} onClose={onClose}>
			<DialogTitle
				sx={{
					backgroundColor: "var(--model-bg-color)",
					color: "var(--text-color)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					py: 1,
				}}
			>
				<span style={{ fontSize: "1.1rem", fontWeight: 600 }}>AI Generated Report</span>
				<div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
					<Button text="Save as PDF" onClick={handleSavePdf} style={{ ...buttonClasses.lynkitOrangeFill, width: "fit-content" }} />
					<IconButton onClick={onClose} sx={{ color: "rgb(158, 158, 158)" }}>
						<CloseIcon />
					</IconButton>
				</div>
			</DialogTitle>
			<DialogContent sx={{ backgroundColor: "#ffffff", padding: 0, overflow: "auto", height: "100vh" }}>
				<iframe
					ref={iframeRef}
					srcDoc={html}
					style={{ width: "100%", height: "100%", border: "none", display: "block" }}
					title="AI Report"
					sandbox="allow-same-origin allow-scripts allow-modals"
				/>
			</DialogContent>
		</MuiDialog>
	);
}

// ─── Component: AI Report Button (Exported) ─────────────────────────────────
export function AIReportButton({ makePayload, isReportLoading, isPDFLoading }) {
	const [fetchReport] = useGetReportMutation({ fixedCacheKey: "getAIReport" });
	const [aiLoading, setAiLoading] = useState(false);
	const [progress, setProgress] = useState({ text: "", percent: 0, page: 0, totalPages: null });
	const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
	const [showReportViewer, setShowReportViewer] = useState(false);
	const [generatedHtml, setGeneratedHtml] = useState("");
	const cancelRef = useRef(false);

	const getApiKey = () => {
		const encrypted = localStorage.getItem("claude_api_key");
		if (!encrypted) return null;
		try {
			return JSON.parse(decryptUsingAES256(encrypted));
		} catch {
			return decryptUsingAES256(encrypted);
		}
	};

	const handleCancel = () => {
		cancelRef.current = true;
	};

	const generateReport = async (apiKey) => {
		cancelRef.current = false;
		setAiLoading(true);
		setProgress({ text: "Initializing...", percent: 0, page: 0, totalPages: null });
		try {
			const { csvData, totalCount, rowCount, truncated } = await fetchAllReportData(makePayload, fetchReport, setProgress, cancelRef);

			if (!csvData || totalCount === 0) {
				toastMessage(false, "No data found for the selected filters");
				return;
			}

			if (cancelRef.current) return;

			setProgress({ text: "Generating AI report... This may take a minute.", percent: 100, page: null, totalPages: null });
			const payload = makePayload("html");
			const reportHtml = await callClaudeAPI(apiKey, csvData, payload, totalCount, rowCount, truncated);
			setGeneratedHtml(reportHtml);
			setShowReportViewer(true);
		} catch (err) {
			if (err.message === "CANCELLED") {
				toastMessage(true, "AI report generation cancelled");
			} else if (err.message === "401") {
				toastMessage(false, "Invalid API key. Please update your Claude API key.");
				localStorage.removeItem("claude_api_key");
			} else if (err.message === "NO_HTML") {
				toastMessage(false, "Failed to generate HTML report from AI response");
			} else {
				toastMessage(false, err.message || "Failed to generate AI report");
			}
		} finally {
			setAiLoading(false);
			setProgress({ text: "", percent: 0, page: 0, totalPages: null });
		}
	};

	const handleClick = () => {
		const apiKey = getApiKey();
		if (!apiKey) {
			setShowApiKeyDialog(true);
			return;
		}
		generateReport(apiKey);
	};

	const handleApiKeySaved = (key) => {
		setShowApiKeyDialog(false);
		generateReport(key);
	};

	return (
		<>
			<Button
				text={aiLoading ? "Generating..." : "AI Report"}
				onClick={handleClick}
				disabled={isReportLoading || isPDFLoading || aiLoading}
				style={buttonClasses.lynkitBlackFill}
			/>

			{aiLoading && (
				<div className="ai-report-loading-overlay">
					<Box sx={{ textAlign: "center", maxWidth: 400, width: "90%" }}>
						<CircularProgress size={50} sx={{ color: "#FF7200", mb: 2 }} />
						<Typography variant="body1" sx={{ color: "#fff", mb: 1, fontWeight: 600 }}>
							{progress.text}
						</Typography>
						{progress.totalPages && (
							<Box sx={{ width: "100%", mt: 2 }}>
								<LinearProgress
									variant="determinate"
									value={progress.percent}
									sx={{
										height: 8,
										borderRadius: 4,
										backgroundColor: "rgba(255,255,255,0.2)",
										"& .MuiLinearProgress-bar": { backgroundColor: "#FF7200", borderRadius: 4 },
									}}
								/>
								<Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5, display: "block" }}>
									{progress.percent}% complete
								</Typography>
							</Box>
						)}
						<Button
							text="Cancel"
							onClick={handleCancel}
							style={{ ...buttonClasses.lynkitOrangeEmpty, color: "#fff", borderColor: "#fff", marginTop: "1.5rem", width: "fit-content" }}
						/>
					</Box>
				</div>
			)}

			{showApiKeyDialog && (
				<ApiKeyDialog
					open={showApiKeyDialog}
					onClose={() => setShowApiKeyDialog(false)}
					onSave={handleApiKeySaved}
				/>
			)}

			{showReportViewer && (
				<AIReportViewer
					open={showReportViewer}
					onClose={() => setShowReportViewer(false)}
					html={generatedHtml}
				/>
			)}
		</>
	);
}
